import { EnumDialogName } from '@calm/evexia-analytics';
import { SxProps, Theme } from '@rhg/libs/material';
import SimpleDialog, {
    SimpleDialogProps,
} from '@rhg/libs/material/simple-dialog';
import {
    IconColor,
    DialogButton,
    BodyTextAlign,
} from '@rhg/libs/material/simple-dialog/types';
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';

export type ChoiceDialogButton = Omit<DialogButton, 'onClick'>;

export interface ChoiceDialogState {
    choose: (options: ChoiceDialogOptions) => Promise<number>;
    isOpen: boolean;
}
export const ChoiceDialogContext = createContext<ChoiceDialogState>(null!);

export type ChoiceDialogOptions = {
    trackDialog?: EnumDialogName;
    body?: string;
    bodyTextAlign?: BodyTextAlign;
    content?: ReactElement | ReactElement[] | null;
    title?: string;
    fullScreen?: boolean;
    hasGradientBackground?: boolean;
    /** Each of these buttons in this array correspond to a choice.
     * The choice passed to the `choose` function will be `1` for the first button `2` for the second ect.
     * The "cancel" dialog button is choice 0 and is not enumerated here.
     */
    buttons?: ChoiceDialogButton[];
    sx?: SxProps<Theme>;
    SimpleDialogProps?: Partial<SimpleDialogProps>;
} & (
    | {
          icon?: ReactElement;
          iconColor?: IconColor;
      }
    | {
          icon?: never;
          iconColor?: never;
      }
);

interface Props {
    children: ReactNode;
}
export const ChoiceDialogProvider = ({ children }: Props) => {
    const [options, setOptions] = useState<ChoiceDialogOptions | undefined>(
        undefined
    );

    const [resolve, setResolve] = useState<
        ((value: number) => void) | undefined
    >(undefined);

    const choose = useCallback((options?: ChoiceDialogOptions) => {
        const promise: Promise<number> = new Promise((resolve) => {
            setOptions(options);
            setResolve(() => resolve);
        });
        return promise;
    }, []);

    const onChoose = useCallback(
        (i: number) => () => {
            resolve?.(i);
            setResolve(undefined);
        },
        [resolve]
    );

    const buttons = useMemo(
        () =>
            options?.buttons?.map<DialogButton>((button, i) => ({
                ...button,
                onClick: onChoose(i + 1),
            })) ?? [],
        [onChoose, options?.buttons]
    );

    return (
        <>
            <ChoiceDialogContext.Provider value={{ choose, isOpen: !!resolve }}>
                {children}
            </ChoiceDialogContext.Provider>
            <SimpleDialog
                trackDialog={options?.trackDialog || 'Confirm'}
                buttons={buttons}
                fullScreen={options?.fullScreen}
                hasGradientBackground={
                    options?.hasGradientBackground ?? options?.fullScreen
                }
                isOpen={!!resolve}
                title={options?.title}
                onClose={onChoose(0)}
                bodyText={options?.body}
                bodyTextAlign={options?.bodyTextAlign}
                content={options?.content}
                icon={options?.icon ?? null}
                iconColor={options?.iconColor}
                {...options?.SimpleDialogProps}
            />
        </>
    );
};
